// Goblin illustrations (optional). The agents are drawn as small clay goblin
// figures: a crew on the login screen, an avatar per role, a figure for the
// moments that matter (waiting on you, nothing to do, a run that failed or
// succeeded) and, now and then, a faint scene behind a card or table.
//
// On by default; the choice is stored per browser and mirrored as
// `data-illustrations` on <html> so CSS-only pieces (scene backgrounds) turn
// off with it. index.html applies it again before first paint.

import { useSyncExternalStore } from 'react'

const STORAGE_KEY = 'goblic.illustrations'
const listeners = new Set<() => void>()

function read(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off'
  } catch {
    return true
  }
}

let enabled = read()
apply(enabled)

function apply(on: boolean): void {
  document.documentElement.dataset.illustrations = on ? 'on' : 'off'
}

export function setIllustrations(on: boolean): void {
  enabled = on
  apply(on)
  try {
    if (on) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, 'off')
  } catch {
    // Storage unavailable (private mode): the choice still applies for this visit.
  }
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useIllustrations(): [boolean, (on: boolean) => void] {
  const value = useSyncExternalStore(subscribe, () => enabled, () => enabled)
  return [value, setIllustrations]
}

// ---------------------------------------------------------------------------
// Assets (public/goblins/)
// ---------------------------------------------------------------------------

export type Moment =
  | 'waiting'
  | 'empty'
  | 'success'
  | 'failed'
  | 'setup'
  | 'sorting'
  | 'bug-hunt'
  | 'column-empty'
  | 'column-empty-people'
  | 'writing'
  | 'reading'
  | 'auditor'
export type Scene = 'workshop' | 'archive' | 'lab' | 'board' | 'library'

const ROLE_AVATARS: Record<string, string> = {
  developer: 'developer',
  reviewer: 'reviewer',
  tester: 'tester',
  'spec writer': 'spec-writer',
  planner: 'planner',
  researcher: 'researcher',
  'product owner': 'product-owner',
  'security reviewer': 'security-reviewer',
}

/** Avatar for a role by its name; custom roles get the generic agent. */
export function roleAvatarUrl(roleName: string | null | undefined): string {
  const key = ROLE_AVATARS[(roleName ?? '').trim().toLowerCase()] ?? 'agent'
  return `/goblins/avatar-${key}.webp`
}

export function momentUrl(moment: Moment): string {
  return `/goblins/${moment}.webp`
}

export const HERO_URL = '/goblins/hero.webp'
